import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, TouchableOpacity, Image} from 'react-native';
import {
  Text, Appbar, Button, ActivityIndicator, Snackbar,
  Dialog, Portal, RadioButton, TextInput,
} from 'react-native-paper';
import {useNavigation, useRoute} from '@react-navigation/native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {taskApi} from '../../services/api/taskApi';
import {workerApi} from '../../services/api/workerApi';
import {orderApi} from '../../services/api/orderApi';
import {colors, spacing} from '../../theme';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const mon = d.toLocaleString('en', {month: 'short'});
  const year = d.getFullYear();
  return `${day} ${mon} ${year}`;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const mon = d.toLocaleString('en', {month: 'short'});
  const year = d.getFullYear();
  const hrs = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${mon} ${year}, ${hrs}:${mins}`;
};

const TaskManagementScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const {
    instanceId, orderId, instanceCode, orderNumber,
    customerName, catalogItemName, deliveryDate, unitPrice,
    expectedCompletionDate, clothImageUrl,
  } = route.params;

  const [tasks, setTasks] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [snackbar, setSnackbar] = useState('');

  // Assign dialog
  const [assignDialog, setAssignDialog] = useState(null);
  const [dialogWorkerId, setDialogWorkerId] = useState(null);

  // Complete dialog
  const [completeDialog, setCompleteDialog] = useState(null);
  const [completeComments, setCompleteComments] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [tasksRes, workersRes] = await Promise.all([
        orderApi.getInstanceTasks(orderId, instanceId),
        workerApi.list(),
      ]);
      setTasks(tasksRes.data.data || []);
      const wd = workersRes.data.data;
      setWorkers(wd?.content || wd || []);
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || 'Failed to load data');
    }
  }, [orderId, instanceId]);

  useEffect(() => {
    setLoading(true);
    loadData().finally(() => setLoading(false));
  }, [loadData]);

  const refreshTasks = useCallback(async () => {
    try {
      const res = await orderApi.getInstanceTasks(orderId, instanceId);
      setTasks(res.data.data || []);
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || 'Failed to refresh');
    }
  }, [orderId, instanceId]);

  const handleAssign = useCallback(async () => {
    if (!assignDialog || !dialogWorkerId) return;
    setActionLoading(assignDialog.taskId);
    try {
      await taskApi.assignWorker(assignDialog.taskId, dialogWorkerId);
      setAssignDialog(null);
      await refreshTasks();
      setSnackbar('Worker assigned');
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || 'Failed to assign');
    } finally {
      setActionLoading(null);
    }
  }, [assignDialog, dialogWorkerId, refreshTasks]);

  const handleStart = useCallback(async (taskId) => {
    setActionLoading(taskId);
    try {
      await taskApi.startTask(taskId);
      await refreshTasks();
      setSnackbar('Task started');
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || 'Failed to start');
    } finally {
      setActionLoading(null);
    }
  }, [refreshTasks]);

  const handleComplete = useCallback(async () => {
    if (!completeDialog) return;
    setActionLoading(completeDialog.taskId);
    try {
      await taskApi.completeTask(completeDialog.taskId, completeComments || null);
      setCompleteDialog(null);
      setCompleteComments('');
      await refreshTasks();
      setSnackbar('Task completed');
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || 'Failed to complete');
    } finally {
      setActionLoading(null);
    }
  }, [completeDialog, completeComments, refreshTasks]);

  const handleCancel = useCallback(async (taskId) => {
    setActionLoading(taskId);
    try {
      await taskApi.cancelTask(taskId);
      await refreshTasks();
      setSnackbar('Task reverted to assigned');
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || 'Failed to cancel');
    } finally {
      setActionLoading(null);
    }
  }, [refreshTasks]);

  const getCircleColor = (status) => colors.taskStatus[status] || colors.taskStatus.PENDING;

  const renderTaskCard = (task, idx) => {
    const status = task.status || 'PENDING';
    const isLoading = actionLoading === task.id;
    const prevCompleted = idx === 0 || (tasks[idx - 1]?.status === 'COMPLETED');

    return (
      <View key={task.id} style={styles.timelineRow}>
        {/* Vertical line + circle */}
        <View style={styles.timelineLeft}>
          {idx > 0 && <View style={[styles.vLine, styles.vLineTop]} />}
          <View style={[styles.vCircle, {backgroundColor: getCircleColor(status)}]}>
            {status === 'COMPLETED' && (
              <MaterialCommunityIcons name="check" size={12} color={colors.white} />
            )}
          </View>
          {idx < tasks.length - 1 && <View style={[styles.vLine, styles.vLineBottom]} />}
        </View>

        {/* Task content */}
        <View style={styles.taskCard}>
          <Text style={styles.taskName}>{task.taskTypeName}</Text>

          {status === 'PENDING' && (
            <>
              {task.scheduledDate && (
                <Text style={styles.taskMeta}>Scheduled On {formatDate(task.scheduledDate)}</Text>
              )}
              <Button
                mode="outlined"
                onPress={() => {
                  setDialogWorkerId(null);
                  setAssignDialog({taskId: task.id, taskName: task.taskTypeName});
                }}
                style={styles.actionBtn}
                textColor={colors.textSecondary}
                loading={isLoading}
                disabled={isLoading}
                compact>
                Assign
              </Button>
            </>
          )}

          {status === 'ASSIGNED' && (
            <>
              {task.scheduledDate && (
                <Text style={styles.taskMeta}>Scheduled On {formatDate(task.scheduledDate)}</Text>
              )}
              {task.assignedWorkerName && (
                <Text style={styles.taskMeta}>Assigned to {task.assignedWorkerName}</Text>
              )}
              {prevCompleted ? (
                <Button
                  mode="contained"
                  onPress={() => handleStart(task.id)}
                  style={styles.actionBtn}
                  buttonColor={colors.primary}
                  loading={isLoading}
                  disabled={isLoading}
                  compact>
                  Start
                </Button>
              ) : (
                <Text style={styles.taskMetaWaiting}>Waiting for previous task to complete</Text>
              )}
            </>
          )}

          {status === 'IN_PROGRESS' && (
            <>
              {task.startedAt && (
                <Text style={styles.taskMeta}>Started On {formatDateTime(task.startedAt)}</Text>
              )}
              {task.assignedWorkerName && (
                <Text style={styles.taskMeta}>Worker: {task.assignedWorkerName}</Text>
              )}
              <View style={styles.actionRow}>
                <Button
                  mode="contained"
                  onPress={() => {
                    setCompleteComments('');
                    setCompleteDialog({taskId: task.id, taskName: task.taskTypeName});
                  }}
                  style={styles.actionBtnFlex}
                  buttonColor={colors.taskStatus.COMPLETED}
                  loading={isLoading}
                  disabled={isLoading}
                  compact>
                  Finish
                </Button>
                <Button
                  mode="outlined"
                  onPress={() => handleCancel(task.id)}
                  style={styles.actionBtnFlex}
                  textColor={colors.error}
                  disabled={isLoading}
                  compact>
                  Cancel
                </Button>
              </View>
            </>
          )}

          {status === 'COMPLETED' && (
            <>
              {task.startedByUserName && task.startedAt && (
                <Text style={styles.taskMeta}>
                  Started By {task.startedByUserName} On {formatDateTime(task.startedAt)}
                </Text>
              )}
              {task.completedByUserName && task.completedAt && (
                <Text style={styles.taskMeta}>
                  Completed By {task.completedByUserName} On {formatDateTime(task.completedAt)}
                </Text>
              )}
              {task.comments && (
                <Text style={styles.taskComment}>"{task.comments}"</Text>
              )}
            </>
          )}
        </View>
      </View>
    );
  };

  const renderVirtualItem = (label, dateStr, idx) => (
    <View key={label} style={styles.timelineRow}>
      <View style={styles.timelineLeft}>
        <View style={[styles.vLine, styles.vLineTop]} />
        <View style={[styles.vCircle, {backgroundColor: colors.border}]}>
          <MaterialCommunityIcons name="flag-outline" size={12} color={colors.textSecondary} />
        </View>
        {idx === 0 && <View style={[styles.vLine, styles.vLineBottom]} />}
      </View>
      <View style={styles.taskCard}>
        <Text style={styles.taskName}>{label}</Text>
        {dateStr && <Text style={styles.taskMeta}>{formatDate(dateStr)}</Text>}
      </View>
    </View>
  );

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content title={instanceCode || 'Task Details'} titleStyle={styles.appbarTitle} />
      </Appbar.Header>

      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerRow}>
          <View style={styles.headerAvatar}>
            {clothImageUrl ? (
              <Image source={{uri: clothImageUrl}} style={styles.headerAvatarImage} resizeMode="cover" />
            ) : (
              <MaterialCommunityIcons name="tshirt-crew-outline" size={28} color={colors.textSecondary} />
            )}
          </View>
          <View style={styles.headerInfo}>
            <Text style={styles.headerOrder}>Order# {orderNumber}</Text>
            <Text style={styles.headerCustomer}>{customerName || 'Unknown'}</Text>
            <Text style={styles.headerCatalogItem}>{catalogItemName || 'N/A'}</Text>
            {unitPrice && (
              <Text style={styles.headerPrice}>{'\u20B9'}{Number(unitPrice).toLocaleString()}</Text>
            )}
          </View>
        </View>
      </View>

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        {tasks.map((task, idx) => renderTaskCard(task, idx))}
        {renderVirtualItem('Expected Completion', expectedCompletionDate, 0)}
        {renderVirtualItem('Expected Delivery', deliveryDate, 1)}
      </ScrollView>

      {/* Assign Worker Dialog */}
      <Portal>
        <Dialog
          visible={!!assignDialog}
          onDismiss={() => setAssignDialog(null)}
          style={styles.dialog}>
          <Dialog.Title>Assign "{assignDialog?.taskName}" to</Dialog.Title>
          <Dialog.ScrollArea style={styles.dialogScroll}>
            <RadioButton.Group
              value={dialogWorkerId ? String(dialogWorkerId) : ''}
              onValueChange={v => setDialogWorkerId(v ? Number(v) : null)}>
              {workers.map(w => (
                <RadioButton.Item
                  key={w.id}
                  label={`${w.fullName}${w.roleName ? ` (${w.roleName})` : ''}`}
                  value={String(w.id)}
                  labelStyle={styles.workerLabel}
                />
              ))}
            </RadioButton.Group>
          </Dialog.ScrollArea>
          <Dialog.Actions>
            <Button onPress={() => setAssignDialog(null)}>Cancel</Button>
            <Button
              onPress={handleAssign}
              disabled={!dialogWorkerId || actionLoading !== null}>
              Assign Worker
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      {/* Complete Task Dialog */}
      <Portal>
        <Dialog
          visible={!!completeDialog}
          onDismiss={() => setCompleteDialog(null)}
          style={styles.dialog}>
          <Dialog.Title>Complete "{completeDialog?.taskName}"</Dialog.Title>
          <Dialog.Content>
            <TextInput
              label="Comments (Optional)"
              value={completeComments}
              onChangeText={setCompleteComments}
              mode="outlined"
              multiline
              numberOfLines={3}
              style={styles.commentInput}
              outlineColor={colors.border}
              activeOutlineColor={colors.primary}
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setCompleteDialog(null)}>Cancel</Button>
            <Button
              onPress={handleComplete}
              disabled={actionLoading !== null}>
              Finish Task
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  header: {
    backgroundColor: colors.surface,
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  headerRow: {flexDirection: 'row'},
  headerAvatar: {
    width: 56,
    height: 56,
    borderRadius: 8,
    backgroundColor: colors.border,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
    overflow: 'hidden',
  },
  headerAvatarImage: {
    width: '100%',
    height: '100%',
  },
  headerInfo: {flex: 1},
  headerOrder: {fontSize: 16, fontWeight: '700', color: colors.primary},
  headerCustomer: {fontSize: 14, color: colors.text, marginTop: 2},
  headerCatalogItem: {fontSize: 13, color: colors.textSecondary, marginTop: 2},
  headerPrice: {fontSize: 14, fontWeight: '600', color: colors.text, marginTop: 4},
  scrollView: {flex: 1},
  scrollContent: {padding: spacing.md, paddingBottom: 40},
  timelineRow: {flexDirection: 'row', minHeight: 60},
  timelineLeft: {
    width: 32,
    alignItems: 'center',
  },
  vLine: {
    width: 2,
    backgroundColor: colors.border,
    flex: 1,
  },
  vLineTop: {flex: 1},
  vLineBottom: {flex: 1},
  vCircle: {
    width: 22,
    height: 22,
    borderRadius: 11,
    justifyContent: 'center',
    alignItems: 'center',
  },
  taskCard: {
    flex: 1,
    backgroundColor: colors.surface,
    borderRadius: 8,
    padding: spacing.sm,
    marginLeft: spacing.sm,
    marginBottom: spacing.sm,
    elevation: 1,
  },
  taskName: {fontSize: 14, fontWeight: '600', color: colors.text},
  taskMeta: {fontSize: 12, color: colors.textSecondary, marginTop: 4},
  taskMetaWaiting: {fontSize: 12, color: colors.textLight, fontStyle: 'italic', marginTop: 4},
  taskComment: {
    fontSize: 12,
    color: colors.textSecondary,
    fontStyle: 'italic',
    marginTop: 4,
  },
  actionBtn: {marginTop: spacing.xs, alignSelf: 'flex-start'},
  actionRow: {flexDirection: 'row', gap: spacing.xs, marginTop: spacing.xs},
  actionBtnFlex: {flex: 1},
  dialog: {maxHeight: '80%'},
  dialogScroll: {maxHeight: 400, paddingHorizontal: 0},
  workerLabel: {fontSize: 14},
  commentInput: {backgroundColor: colors.surface},
});

export default TaskManagementScreen;
